export type GrowthDimensionId =
  | "attr-career"
  | "attr-wealth"
  | "attr-experience"
  | "attr-physique";

export type GrowthGrade = "E" | "D" | "C" | "B" | "A";
export type GrowthUnit = "XP" | "CNY";

export interface GrowthStageDefinition {
  name: string;
  threshold: number;
}

export interface GrowthStagePreview extends GrowthStageDefinition {
  reached: boolean;
  current: boolean;
}

export interface GrowthDimensionSnapshot {
  id: GrowthDimensionId;
  title: string;
  sort_order: number;
  evidence_count: number;
  basis: string;
  value: number;
  unit: GrowthUnit;
  stage_name: string;
  stage_number: number;
  stage_count: number;
  stage_progress: number;
  grade: GrowthGrade;
  grade_progress: number;
  next_stage_name: string | null;
  next_target: number | null;
  remaining: number | null;
  stages: GrowthStagePreview[];
}

export const GROWTH_DIMENSIONS: ReadonlyArray<{
  id: GrowthDimensionId;
  title: string;
  sortOrder: number;
  unit: GrowthUnit;
}> = [
  { id: "attr-career", title: "事业", sortOrder: 1, unit: "XP" },
  { id: "attr-wealth", title: "财富", sortOrder: 2, unit: "CNY" },
  { id: "attr-experience", title: "经历", sortOrder: 3, unit: "XP" },
  { id: "attr-physique", title: "体魄", sortOrder: 4, unit: "XP" }
];

export const CAREER_KEYWORDS = [
  "工作", "事业", "业务", "客户", "订单", "外贸", "询盘", "报价", "打样", "生产", "发货",
  "项目", "交付", "开发", "销售", "谈判", "回款"
] as const;

export const PHYSIQUE_KEYWORDS = [
  "运动", "健身", "跑步", "游泳", "睡眠", "体重", "健康", "锻炼", "体能", "力量", "有氧",
  "骑行", "徒步", "篮球", "足球", "羽毛球", "乒乓球", "瑜伽"
] as const;

export const PROJECT_PROGRESS_POINTS: Readonly<Record<string, number>> = {
  PLANNED: 2,
  ACTIVE: 8,
  BLOCKED: 4,
  ON_HOLD: 4,
  COMPLETED: 80,
  ARCHIVED: 80,
  CANCELLED: 0
};

export const ORDER_PROGRESS_POINTS: Readonly<Record<string, number>> = {
  INQUIRY: 2,
  REQUIREMENT_CONFIRMED: 5,
  QUOTING: 8,
  SAMPLING: 12,
  SAMPLE_CONFIRMATION: 18,
  DEPOSIT_PENDING: 22,
  PRODUCTION: 30,
  QUALITY_CHECK: 38,
  BALANCE_PENDING: 45,
  SHIPPING: 55,
  COMPLETED: 100,
  PAUSED: 2,
  CANCELLED: 0
};

export const GROWTH_STAGES: Record<GrowthDimensionId, readonly GrowthStageDefinition[]> = {
  "attr-career": [
    { name: "职业起步", threshold: 0 },
    { name: "稳定交付", threshold: 300 },
    { name: "独立担当", threshold: 1_200 },
    { name: "业务骨干", threshold: 4_000 },
    { name: "领域专家", threshold: 12_000 },
    { name: "系统经营者", threshold: 30_000 },
    { name: "行业影响", threshold: 70_000 },
    { name: "时代级事业", threshold: 150_000 }
  ],
  "attr-wealth": [
    { name: "原始积累", threshold: 0 },
    { name: "小有积蓄", threshold: 100_000 },
    { name: "小有资产", threshold: 500_000 },
    { name: "百万基石", threshold: 1_000_000 },
    { name: "富裕人生", threshold: 5_000_000 },
    { name: "财务从容", threshold: 10_000_000 },
    { name: "资本经营", threshold: 30_000_000 },
    { name: "产业影响", threshold: 100_000_000 }
  ],
  "attr-experience": [
    { name: "开始记录", threshold: 0 },
    { name: "主动探索", threshold: 250 },
    { name: "经验成形", threshold: 1_000 },
    { name: "见多识广", threshold: 3_000 },
    { name: "跨界融通", threshold: 8_000 },
    { name: "丰厚人生", threshold: 18_000 },
    { name: "人生作品", threshold: 40_000 },
    { name: "时代见证", threshold: 90_000 }
  ],
  "attr-physique": [
    { name: "重新启动", threshold: 0 },
    { name: "稳定运动", threshold: 200 },
    { name: "健康体能", threshold: 800 },
    { name: "强健体魄", threshold: 2_500 },
    { name: "高阶运动者", threshold: 7_000 },
    { name: "专项精英", threshold: 16_000 },
    { name: "竞技巅峰", threshold: 40_000 },
    { name: "人类极限", threshold: 100_000 }
  ]
};

const GRADES: readonly GrowthGrade[] = ["E", "D", "C", "B", "A"];

function boundedPercent(value: number) {
  return Math.max(0, Math.min(100, Math.round(value * 10) / 10));
}

export function createGrowthSnapshot(input: {
  id: GrowthDimensionId;
  value: number;
  evidenceCount: number;
  basis: string;
}): GrowthDimensionSnapshot {
  const definition = GROWTH_DIMENSIONS.find((item) => item.id === input.id);
  if (!definition) throw new Error(`Unknown growth dimension: ${input.id}`);

  const stages = GROWTH_STAGES[input.id];
  const value = Math.max(0, input.value);
  let stageIndex = 0;
  for (let index = 0; index < stages.length; index += 1) {
    if (value >= stages[index]!.threshold) stageIndex = index;
  }

  const currentStage = stages[stageIndex]!;
  const nextStage = stages[stageIndex + 1] ?? null;
  const finalReference = currentStage.threshold * 5 || 1;
  const stageCeiling = nextStage?.threshold ?? finalReference;
  const stageSpan = Math.max(1, stageCeiling - currentStage.threshold);
  const stageProgress = boundedPercent(
    ((value - currentStage.threshold) / stageSpan) * 100
  );
  const gradeIndex = Math.min(GRADES.length - 1, Math.floor(stageProgress / 20));
  const gradeStart = gradeIndex * 20;
  const gradeProgress = stageProgress >= 100
    ? 100
    : boundedPercent(((stageProgress - gradeStart) / 20) * 100);

  return {
    id: definition.id,
    title: definition.title,
    sort_order: definition.sortOrder,
    evidence_count: Math.max(0, Math.round(input.evidenceCount)),
    basis: input.basis,
    value,
    unit: definition.unit,
    stage_name: currentStage.name,
    stage_number: stageIndex + 1,
    stage_count: stages.length,
    stage_progress: stageProgress,
    grade: GRADES[gradeIndex]!,
    grade_progress: gradeProgress,
    next_stage_name: nextStage?.name ?? null,
    next_target: nextStage?.threshold ?? null,
    remaining: nextStage ? Math.max(0, nextStage.threshold - value) : null,
    stages: stages.map((stage, index) => ({
      ...stage,
      reached: value >= stage.threshold,
      current: index === stageIndex
    }))
  };
}
